import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import CommonGrid from "../../../../Repository/CommonGrid";
import { useTranslation } from "react-i18next";
import { cloneDeep, debounce } from "lodash";
import LightTooltip from "../../../../../commonComponents/LightTooltip";
import { styles } from "./ServiceDetailsTab";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";

const OutValuesTab = ({
  submitServiceDetails,
  screen,
}) => {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState(null);
  const [currentOutValues, setCurrentOutValues] = useState([]);
  const [valueName, setValueName] = useState("");
  const [selectedOption, setSelectedOption] = useState("useAsResult");
  const [selectedArgName, setSelectedArgName] = useState("");
  const currentSelectedRow = useRef({});
  const parentScreen = useContext(ParentScreenCtx);
  const selectedNode = useSelector(
    (state) => state.treeData[parentScreen].rightTree.tree.body.selectedNode
  );
  
  // Dynamic reference for INBOUND/OUTBOUND based on screen type
  const nodeData = selectedNode?.[screen === 'GET_DATA' ? 'INBOUND' : 'OUTBOUND'];

  // Separate grid updates from Redux updates
  const updateGridOnly = useCallback((data = [], selectionIndex, gridAPI) => {
    if (!gridAPI) {
      setCurrentOutValues([]);
      return;
    }

    const rowData = data.map((item, index) => ({
      ...item,
      id: index,
    }));

    gridAPI.setGridOption("rowData", rowData);
    setCurrentOutValues(rowData);

    setTimeout(() => {
      gridAPI.forEachNode((node) => {
        if (node?.data?.id === selectionIndex) {
          node.setSelected(true);
          gridAPI.ensureNodeVisible(node);
        }
      });
    }, 10);
  }, []);

  // Debounced Redux update
  const debouncedSubmit = useCallback(
    debounce((data) => {
      const argsData = data.map(({ ARGNAME, ARGTYPE, WRITE_TO_RESPONSE, MAPNAME }) => ({
        ARGNAME,
        ARGTYPE,
        WRITE_TO_RESPONSE, 
        MAPNAME,
      }));
      submitServiceDetails({
        add: {
          ARGS: [
            ...(nodeData?.ARGS?.filter(
              (item) => item.ARGTYPE === "IN"
            ) || []),
            ...argsData,
          ],
        },
      });
    }, 300),
    [submitServiceDetails, nodeData]
  );

  const deleteActionHandler = (info) => {
    const data = [];
    info.api.forEachNode((node) => {
      if (info.data.id !== node.data.id) data.push(node.data);
    });
    debouncedUpdateGridData([...data], 0, info.api);
  };

  const CustomCellRenderer = (info) => {
    const { value } = info;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "2rem",
        }}
      >
        <div className="truncated-text" title={value}>
          {value}
        </div>
        <div className="flex align-center">
          <LightTooltip onClick={() => deleteActionHandler(info)}>
            <span style={{ cursor: "pointer" }} className="DELETE_MUI"></span>
          </LightTooltip>
        </div>
      </div>
    );
  };

  const customClickHandlerOnAdd = (e) => {
    const colIdArr = [];
    e.api.forEachNode((node) => colIdArr.push(node.data.id));
    const newColId = colIdArr.length > 0 ? Math.max(...colIdArr) + 1 : 0;

    const newRow = {
      ARGNAME: "UNNAMED",
      ARGTYPE: "OUT",
      WRITE_TO_RESPONSE: "TRUE",
      id: newColId,
    };

    const data = [];
    const selectedRows = e.api.getSelectedRows();
    e.api.forEachNode((node) => {
      data.push(node.data);
    });

    data.splice(+(selectedRows?.[0]?.id || 0) + 1, 0, newRow);
    debouncedUpdateGridData([...data], newColId, e.api);
  };

  const gridOptions = {
    columnDefs: [
      {
        addNewControl: true,
        addControlLabel: t("MNU_WS_NEW_OUT_VALUE"),
        headerName: "",
        field: "ARGNAME",
        customClickHandlerOnAdd: customClickHandlerOnAdd,
        cellRenderer: (info) => CustomCellRenderer(info),
        editable: false,
        resizable: false,
        flex: 1,
      },
    ],
    suppressMovableColumns: true,
  };

  function handleDataUpdate(key, value, bulk) {
    const data = cloneDeep(currentOutValues);
    if (!bulk) {
      if (value) data[currentSelectedRow.current.id][key] = value;
      else
        data[currentSelectedRow.current.id].hasOwnProperty(key) &&
          delete data[currentSelectedRow.current.id][key];
    } else {
      Object.keys(bulk).forEach((e) => {
        if (bulk[e]) data[currentSelectedRow.current.id][e] = bulk[e];
      });
    }
    
    currentSelectedRow.current = data[currentSelectedRow.current.id];
    debouncedUpdateGridData(data, currentSelectedRow.current.id, gridApi);
  }

  const debouncedUpdateGridData = useCallback(
    debounce((data = [], selectionIndex, gridAPI) => {
      
      updateGridOnly(data, selectionIndex, gridAPI);
      if (gridAPI) {
        const argsData = data.map(({ ARGNAME, ARGTYPE, WRITE_TO_RESPONSE, MAPNAME }) => ({
          ARGNAME,
          ARGTYPE,
          WRITE_TO_RESPONSE, 
          MAPNAME,
        }));

        submitServiceDetails({
          add: {
            ARGS: [
              ...argsData,
              ...(nodeData?.ARGS?.filter(
                (item) => item.ARGTYPE === "IN"
              ) || []),
            ],
          },
        });
      }
    }, 300),
    [updateGridOnly, submitServiceDetails, nodeData]
  );

  const handleRow = (options) => {
    const selectedRow = options.api.getSelectedRows()[0];
    if (!selectedRow) return;
    currentSelectedRow.current = { ...selectedRow };
    setSelectedArgName(selectedRow?.ARGNAME || "");
    setSelectedOption(
      selectedRow?.WRITE_TO_RESPONSE === "TRUE"
        ? "useAsResult"
        : "addToWorkflowSession"
    );
    setValueName(selectedRow?.MAPNAME || "");
    options.api.redrawRows();
  };

  const handleRadioChange = (event) => {
    const currSelectedOption = event.target.value;
    setSelectedOption(currSelectedOption);

    if(currSelectedOption === "addToWorkflowSession"){
      // submitServiceDetails({ add: { WRITE_TO_RESPONSE: null } });
      // setValueName(currentSelectedRow.current?.MAPNAME || "");
      handleDataUpdate("WRITE_TO_RESPONSE", null);
    }
    else
      handleDataUpdate(null, null, {
        WRITE_TO_RESPONSE: "TRUE",
        MAPNAME: null
      });
      // submitServiceDetails({add: { WRITE_TO_RESPONSE: "TRUE", MAPNAME: null }});
  };

  // Handle outValues updates
  // useEffect(() => {
  //   if (gridApi) {
  //     const formattedData = outValues.map((name) => ({
  //       ARGNAME: name,
  //       ARGTYPE: "OUT",
  //       WRITE_TO_RESPONSE: true,
  //     }));
  //     updateGridOnly(formattedData, 0, gridApi); // Use updateGridOnly for initial load
  //   }
  // }, [outValues, gridApi, updateGridOnly]);

  // Initialize grid with connectionDetails
  useEffect(() => {
    if (gridApi && nodeData?.ARGS?.length > 0) {
      const outArgs = nodeData.ARGS.filter(
        (item) => item.ARGTYPE === "OUT"
      );
      updateGridOnly(outArgs, currentSelectedRow?.current?.id || 0, gridApi); // Use updateGridOnly to prevent double updates
    }
  }, [selectedNode?.key, gridApi, updateGridOnly]);

  return (
    <div style={styles.serviceContainer}>
      <div className="width-100" style={{ height: "220px" }}>
        <CommonGrid
          gridOptions={gridOptions}
          rowData={currentOutValues}
          getGridAPI={(e, gridApi) => {
            setGridApi(gridApi.api);
          }}
          containerStyle={{ height: "100% !important" }}
          gridResizable={true}
          gridProps={{
            rowSelection: "single",
            onSelectionChanged: handleRow,
          }}
        />
      </div>

      {
        currentOutValues?.length > 0 &&
        <>
          <TextField
            label={t("LBL_NAME")}
            variant="outlined"
            className="width-100"
            size="small"
            value={selectedArgName}
            name="ARGNAME"
            disabled={nodeData?.USE_WSDL === "TRUE" ? true : false}
            onChange={(e) => {
              setSelectedArgName(e.target.value);
              handleDataUpdate("ARGNAME", e.target.value);
            }}
          />

          <FormControl style={{ alignSelf: "start" }}>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              name="radio-buttons-group"
              value={selectedOption}
              onChange={handleRadioChange}
            >
              <FormControlLabel
                value="useAsResult"
                control={<Radio />}
                label={t("BTN_WS_USE_AS_RESULT")}
                disabled={currentOutValues.length === 0}
              />
              <FormControlLabel
                value="addToWorkflowSession"
                control={<Radio />}
                label={t("BTN_ADD_TO_WF_SESSION")}
                disabled={currentOutValues.length === 0}
              />
            </RadioGroup>
          </FormControl>

          <TextField
            label={t("LBL_WS_VALUE_NAME")}
            variant="outlined"
            className="width-100"
            size="small"
            name="MAPNAME"
            value={valueName || ""}
            onChange={(e) => {
              setValueName(e.target.value);
              handleDataUpdate("MAPNAME", e.target.value);
            }}
            disabled={(selectedOption !== "addToWorkflowSession" || currentOutValues.length === 0)}
          />
        </>
      }
    </div>
  );
};

export default OutValuesTab;
